import entityMappings from '../config/entityMappings.json';

export interface EntityInfo {
  original: string;
  canonical: string;
  category: string;
  categoryLabel?: string;
  categoryIcon?: string;
  categoryColor?: string;
}

export interface CategorizedEntities {
  [category: string]: {
    label: string;
    icon: string;
    color: string;
    entities: Array<{
      name: string;
      count: number;
      canonical: string;
    }>;
  };
}

/**
 * Normalizes an entity name to its canonical form
 */
export function normalizeEntity(entityName: string): EntityInfo {
  // Check explicit mappings first
  for (const mappingCategory of Object.values(entityMappings.mappings)) {
    for (const mapping of Object.values(mappingCategory)) {
      if (mapping.alternatives.includes(entityName)) {
        const category = mapping.category;
        const categoryInfo = entityMappings.categories[category];
        
        return {
          original: entityName,
          canonical: mapping.canonical,
          category: category,
          categoryLabel: categoryInfo?.label,
          categoryIcon: categoryInfo?.icon,
          categoryColor: categoryInfo?.color
        };
      }
    }
  }
  
  // If no explicit mapping, try auto-categorization
  const category = autoCategorizEntity(entityName);
  const categoryInfo = category ? entityMappings.categories[category] : undefined;
  
  return {
    original: entityName,
    canonical: entityName, // No normalization if not in mappings
    category: category || 'other',
    categoryLabel: categoryInfo?.label || 'Other',
    categoryIcon: categoryInfo?.icon || '📍',
    categoryColor: categoryInfo?.color || '#9ca3af'
  };
}

/**
 * Auto-categorizes an entity based on pattern matching
 */
function autoCategorizEntity(entityName: string): string | null {
  const lowerName = entityName.toLowerCase();
  
  for (const rule of entityMappings.rules.autoCategorizationPatterns) {
    const regex = new RegExp(rule.pattern, rule.caseSensitive ? '' : 'i');
    if (regex.test(entityName)) {
      return rule.category;
    }
  }
  
  return null;
}

/**
 * Groups entities by their canonical names and counts occurrences
 */
export function groupEntities(entities: string[]): Map<string, { count: number; originals: string[] }> {
  const grouped = new Map<string, { count: number; originals: string[] }>();
  
  for (const entity of entities) {
    const normalized = normalizeEntity(entity);
    const existing = grouped.get(normalized.canonical);
    
    if (existing) {
      existing.count++;
      if (!existing.originals.includes(entity)) {
        existing.originals.push(entity);
      }
    } else {
      grouped.set(normalized.canonical, {
        count: 1,
        originals: [entity]
      });
    }
  }
  
  return grouped;
}

/**
 * Categorizes entities for display in UI
 */
export function categorizeEntities(entities: string[]): CategorizedEntities {
  const categorized: CategorizedEntities = {};
  const entityCounts = new Map<string, number>();
  
  // Count occurrences
  for (const entity of entities) {
    entityCounts.set(entity, (entityCounts.get(entity) || 0) + 1);
  }
  
  // Group by canonical names
  const grouped = groupEntities(entities);
  
  // Categorize
  for (const [canonical, data] of grouped.entries()) {
    const entityInfo = normalizeEntity(data.originals[0]);
    const category = entityInfo.category || 'other';
    
    if (!categorized[category]) {
      const categoryInfo = entityMappings.categories[category] || {
        label: 'Other',
        icon: '📍',
        color: '#9ca3af'
      };
      
      categorized[category] = {
        label: categoryInfo.label,
        icon: categoryInfo.icon,
        color: categoryInfo.color,
        entities: []
      };
    }
    
    categorized[category].entities.push({
      name: canonical,
      count: data.count,
      canonical: canonical
    });
  }
  
  // Sort entities within each category by count (descending)
  for (const category of Object.values(categorized)) {
    category.entities.sort((a, b) => b.count - a.count);
  }
  
  return categorized;
}

/**
 * Gets unique canonical entities from a list
 */
export function getUniqueCanonicalEntities(entities: string[]): string[] {
  const canonicalSet = new Set<string>();
  
  for (const entity of entities) {
    const normalized = normalizeEntity(entity);
    canonicalSet.add(normalized.canonical);
  }
  
  return Array.from(canonicalSet).sort();
}

/**
 * Checks if two entities are the same (have the same canonical form)
 */
export function areEntitiesSame(entity1: string, entity2: string): boolean {
  const normalized1 = normalizeEntity(entity1);
  const normalized2 = normalizeEntity(entity2);
  return normalized1.canonical === normalized2.canonical;
}

/**
 * Gets all alternative names for a canonical entity
 */
export function getEntityAlternatives(canonicalName: string): string[] {
  for (const mappingCategory of Object.values(entityMappings.mappings)) {
    for (const mapping of Object.values(mappingCategory)) {
      if (mapping.canonical === canonicalName) {
        return mapping.alternatives;
      }
    }
  }
  
  return [canonicalName];
}